/**
 * Channel Order Import Renderer (TV1-2379)
 *
 * The channel ORDER seeding run: the seller's FULL past order history, pulled in
 * one batch process when a channel is connected.
 *
 * Its own renderer rather than a reuse of `ChannelPublicationImportRenderer`,
 * even though the data shape is identical (connector + integration user). What
 * differs is the sentence a reader needs: an order seeding run imports records
 * that are deliberately INERT — no invoicing, no stock movement, no messages to
 * the buyer, no writes back to the channel — and a "importa desde un canal"
 * summary borrowed from the publication import would say nothing about that.
 * The one statement worth the panel is the one a shared renderer cannot carry.
 *
 * Data shape (from ChannelOrderImportBatchProcessStartedEventSchema):
 *   connectorId: string
 *   integrationUserId: string
 */

import { FC } from 'react';
import { Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconPlug } from '@tabler/icons-react';
import type { DetailRendererProps, OperationRendererConfig, ProcessData } from './index';

const DetailComponent: FC<DetailRendererProps> = ({ process, resolved, t }) => {
  const data = ( process.data || {}) as ProcessData;
  const connectorId = data.connectorId;
  const resolvedChannel = connectorId ? resolved.resolvedChannels[connectorId] : undefined;

  return (
    <Stack gap="sm">
      <Text fz="sm" c="dimmed">
        {t( 'batchProcesses_operations_ChannelOrderImport_description' )}
      </Text>

      <Group gap="xs">
        <Text fz="sm" fw={500}>
          {t( 'batchProcesses_renderer_sourceChannel' )}:
        </Text>
        <ThemeIcon size="sm" variant="light" color="gray">
          <IconPlug size={14} />
        </ThemeIcon>
        <Text fz="sm">
          {resolvedChannel?.name ?? connectorId ?? t( 'batchProcesses_renderer_none' )}
        </Text>
      </Group>
    </Stack>
  );
};

export const config: Omit<OperationRendererConfig, 'icon' | 'color' | 'labelKey'> = {
  getSummary: ( data, t, _process, resolvedChannels ) => {
    const connectorId = data?.connectorId;
    if ( connectorId ) {
      const resolved = resolvedChannels?.[connectorId];
      const channelName = resolved?.name ?? connectorId;
      return t( 'batchProcesses_summary_ordersFromChannel', { channel: channelName });
    }
    return t( 'batchProcesses_summary_unknownOperation' );
  },
  DetailComponent,
  getRequiredReferenceIds: ( process ) => {
    const data = ( process?.data || {}) as ProcessData;
    return {
      channelIds: data.connectorId ? [data.connectorId] : []
    };
  }
};

export default DetailComponent;
